import React, { useState } from 'react';
import { X, MessageSquare, Star, Mail, ChevronDown } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { Input } from 'components/ui/input';
import { Alert, AlertDescription } from 'components/ui/alert';
import { cn } from 'lib/utils';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: {
    rating: number;
    message: string;
    contactEmail?: string;
    contactPhone?: string;
  }) => Promise<void>;
}

const ratingLabels: { [key: number]: string } = {
  0: 'Rate your experience',
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent',
};

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [message, setMessage] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please rate your experience');
      return;
    }

    if (!message.trim()) {
      setError('Please provide detailed feedback to help us improve');
      return;
    }

    if (message.trim().length < 20) {
      setError('Please provide more detailed feedback (at least 20 characters)');
      return;
    }

    // Validate email if provided
    if (contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate phone if provided
    if (contactPhone && !/^[\d\s\-+()]+$/.test(contactPhone)) {
      setError('Please enter a valid phone number');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        rating,
        message: message.trim(),
        contactEmail: contactEmail.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
      });
      setSuccess(true);
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to submit feedback. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoveredRating(0);
    setMessage('');
    setShowContact(false);
    setContactEmail('');
    setContactPhone('');
    setError(null);
    setSuccess(false);
    onClose();
  };

  const displayRating = hoveredRating || rating;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent
        className={cn(
          'max-w-[600px] max-h-[90vh] overflow-y-auto p-0 gap-0 [&>button]:hidden',
          'rounded-lg border shadow-lg'
        )}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-4 border-b bg-card">
          <DialogHeader className="flex-1 gap-0 p-0 text-left">
            <DialogTitle className="flex items-center gap-2 text-xl font-semibold leading-tight text-foreground-strong">
              <MessageSquare size={20} className="text-primary" />
              Share Your Feedback
            </DialogTitle>
          </DialogHeader>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleClose}
            aria-label="Close"
            className="shrink-0 h-8 w-8 rounded-lg"
          >
            <X size={16} />
          </Button>
        </div>

        <div className="flex flex-col gap-5 px-6 py-6">
          <section className="flex flex-col gap-3">
            <h3 className="text-lg font-semibold text-foreground-strong">
              How would you rate your experience?
            </h3>
            <DialogDescription className="text-foreground-muted text-sm leading-relaxed">
              Your feedback helps us improve EIR Space. Please share your thoughts
              in detail below.
            </DialogDescription>
            <div className="flex gap-1.5 justify-center items-center py-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
 key={star}
 type="button"
 className="cursor-pointer p-1 transition-transform bg-transparent border-none flex items-center justify-center"
 onClick={() => {
                    setRating(star);
                    setError(null);
                  }}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  aria-label={`${star} star${star > 1 ? 's' : ''}`}
                >
                  <Star
                    size={36}
                    strokeWidth={2}
                    className={cn(
                      'transition-colors',
                      star <= displayRating
                        ? 'fill-amber-400 text-amber-400'
                        : 'fill-none text-slate-200'
                    )}
                  />
                </button>
              ))}
            </div>
            <div
 className={cn(
 'text-center text-sm font-semibold min-h-[1.2rem] mt-1',
 displayRating === 0 && 'text-foreground-muted',
 displayRating >= 1 && displayRating <= 2 && 'text-red-500',
                displayRating === 3 && 'text-amber-500',
                displayRating >= 4 && 'text-emerald-600'
              )}
            >
              {ratingLabels[displayRating]}
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h3 className="text-lg font-semibold text-foreground-strong">
              {rating === 0
                ? 'Tell us about your experience'
                : rating <= 2
                  ? 'What went wrong? How can we improve?'
                  : rating <= 3
                    ? 'What could be better? Any suggestions?'
                    : 'What did you like? What else could we add?'}
            </h3>
            <DialogDescription className="text-foreground-muted text-sm leading-relaxed">
              Please be as detailed as possible. Share specific features you liked
              or issues you encountered. Your detailed feedback helps us make EIR
              Space better for everyone.
            </DialogDescription>
            <Textarea
              placeholder={
                rating === 0
                  ? "Start by rating your experience above, then share your thoughts here..."
                  : rating <= 2
                    ? "Please describe what didn't work well, what was confusing, or what you expected but didn't find. Be specific about which features or parts of the app caused issues..."
                    : rating <= 3
                      ? "What aspects could be improved? Are there features you'd like to see? What would make your experience better?"
                      : "What did you enjoy most? Which features are most valuable to you? Any suggestions for new features or improvements?"
              }
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                setError(null);
              }}
              maxLength={2000}
              className="min-h-[100px] rounded-lg border px-3.5 py-3 text-sm resize-y focus-visible:border-primary focus-visible:ring-primary/20"
            />
            <div className="text-right text-xs text-foreground-muted">
              {message.length}/2000 characters{' '}
              {message.length < 20 &&
                message.length > 0 &&
                '(minimum 20 characters)'}
            </div>
          </section>

          <div className="flex flex-col gap-3 p-4 bg-muted rounded-lg border">
            <button
 type="button"
 className="flex items-center gap-2 bg-transparent border-none cursor-pointer p-2 text-primary text-sm font-medium hover:text-primary/90 transition-colors"
 onClick={() => setShowContact(!showContact)}
            >
              <Mail size={16} />
              {showContact
                ? 'Hide contact information'
                : 'Add contact information (optional)'}
              <ChevronDown
                size={16}
                className={cn('transition-transform', showContact && 'rotate-180')}
              />
            </button>

            {showContact && (
              <>
                <Input
                  type="email"
                  placeholder="Email address (optional)"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  className="rounded-lg border border focus-visible:border-primary focus-visible:ring-primary/20"
                />
                <Input
                  type="tel"
                  placeholder="Phone number (optional)"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  className="rounded-lg border border focus-visible:border-primary focus-visible:ring-primary/20"
                />
                <div className="rounded-lg border-l-4 border-primary bg-muted p-3 text-xs text-foreground-muted leading-relaxed">
                  <strong>Privacy Note:</strong> We don't store any personal data
                  about you, so we won't know who you are. However, we do store
                  your feedback in our database so we can review it and improve
                  the app. If you provide contact information, we'll only use it
                  to follow up on your feedback if needed. Your feedback message
                  will be stored, but your contact information is optional and
                  separate.
                </div>
              </>
            )}
          </div>

          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="rounded-lg border-emerald-200 bg-emerald-50 text-emerald-800">
              <AlertDescription>
                Thank you for your detailed feedback! We appreciate you taking
                the time to help us improve.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={
                isSubmitting ||
                rating === 0 ||
                !message.trim() ||
                message.trim().length < 20
              }
              className="disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Sending...' : 'Submit Feedback'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
